import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FaqList, { type FaqItem } from "@/components/feature/FaqList";
import CategoryDropdown from "./components/CategoryDropdown";
import CategoryGrid from "./components/CategoryGrid";
import ArticlesGrid from "./components/ArticlesGrid";
import { MAIN_SITE_URL } from "@/mocks/categories";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Warm%20abstract%20product%20collage%20of%20folded%20clothing%20a%20pair%20of%20sneakers%20and%20a%20leather%20handbag%20arranged%20in%20a%20soft%20diagonal%20flow%20on%20a%20terracotta%20and%20warm%20cream%20gradient%20background%20stylized%20editorial%20still%20life%20with%20gentle%20shadows%20high%20detail&width=1600&height=900&seq=hero-home&orientation=landscape&nocache=true";

export default function Home() {
  const { t } = useTranslation();

  const features = [1, 2, 3, 4].map((i) => ({
    icon: ["ri-links-line", "ri-store-2-line", "ri-shield-check-line", "ri-book-open-line"][i - 1],
    title: t(`features.t${i}`),
    desc: t(`features.d${i}`),
  }));

  const faqItems: FaqItem[] = [1, 2, 3, 4, 5, 6].map((i) => ({
    q: t(`faq.q${i}`),
    a: t(`faq.a${i}`),
  }));

  return (
    <>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[560px] md:min-h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AllChinaBuy Spreadsheet — products and buying resources"
            title="AllChinaBuy Spreadsheet — products and buying resources"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />

          <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-28 flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs md:text-sm font-medium mb-5">
              <i className="ri-sparkling-2-line text-sm" />
              {t("hero.badge")}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-background-50 leading-tight tracking-tight max-w-3xl">
              {t("hero.title")}
            </h1>
            <p className="mt-3 text-base md:text-lg font-medium text-background-50/95">
              {t("hero.subtitle")}
            </p>
            <p className="mt-5 text-sm md:text-base text-background-50/80 leading-relaxed max-w-2xl">
              {t("hero.description")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                {t("hero.cta.primary")}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-foreground-900 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
              >
                {t("hero.cta.secondary")}
              </a>
            </div>

            <div className="mt-8 w-full flex justify-center">
              <CategoryDropdown />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">
                {t("categories.title")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                {t("categories.subtitle")}
              </p>
            </div>
            <CategoryGrid />
          </div>
        </section>

        {/* Intro */}
        <section id="intro" className="px-4 md:px-6 py-14 md:py-20 bg-background-100/60">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {t("intro.title")}
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {t("intro.p1")}
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {t("intro.p2")}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {t("intro.p3")}
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-10">
              {t("features.title")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="p-5 rounded-lg border border-background-200 bg-background-50"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 mb-4">
                    <i className={`${f.icon} text-xl`} />
                  </span>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pillar articles */}
        <section id="articles" className="px-4 md:px-6 py-14 md:py-20 bg-background-100/60">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950">
                {t("articles.title")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600">
                {t("articles.subtitle")}
              </p>
            </div>
            <ArticlesGrid />
          </div>
        </section>

        {/* Article card */}
        <section id="guide" className="px-4 md:px-6 py-14 md:py-20 bg-background-100/60">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-6 p-6 md:p-8 rounded-lg border border-background-200 bg-background-50">
              <div className="w-full md:w-1/3">
                <div className="w-full aspect-square rounded-lg bg-secondary-100 flex items-center justify-center">
                  <i className="ri-article-line text-6xl text-secondary-500" />
                </div>
              </div>
              <div className="w-full md:w-2/3">
                <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                  {t("articleCard.label")}
                </span>
                <h2 className="mt-3 text-xl md:text-2xl font-bold text-foreground-950">
                  {t("articleCard.title")}
                </h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {t("articleCard.excerpt")}
                </p>
                <Link
                  to="/spreadsheet-guide"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors"
                >
                  {t("articleCard.read")}
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-10">
              {t("faq.title")}
            </h2>
            <FaqList items={faqItems} />
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-accent-100/50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {t("cta.title")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-700">
              {t("cta.description")}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                {t("cta.primary")}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-foreground-900 text-sm md:text-base font-semibold whitespace-nowrap border border-background-200 hover:bg-background-100 transition-colors"
              >
                {t("cta.secondary")}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}